import {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import SearchBar from '../SearchBar'
import SEARCH_PAGE_CONFIG from '../../../pages/SearchPage/config'
import { SearchType } from '../../../pages/SearchPage/constants'
import { queryParam } from '../../../pages/SearchPage/query-params'
import toSearchParams from '../../utils/toSearchParams'
import { useHeaderSearch } from '../../contexts/HeaderSearch/HeaderSearchContext'
import useTraverseList from '../../hooks/useTraverseList'
import type { AutoSuggestSearchResult } from './AutoSuggest/AutoSuggest'
import AutoSuggest from './AutoSuggest/AutoSuggest'
import { getGroups, isAuthenticated } from '../../utils/auth/auth'
import { fetchSuggestionsFromSearchApi, MIN_QUERY_LENGTH } from '../../utils/searchUtils'
import { toSearch } from '../../../links'
import srOnlyStyle from '../../utils/srOnlyStyle'

const ESC_KEY = 'Escape'

const StyledLegend = styled.legend`
  ${srOnlyStyle}
`

const StyledForm = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`

const ACTIVE_ITEM_CLASS = 'auto-suggest__dropdown-item--active'
export const SEARCH_BAR_INPUT_ID = 'auto-suggest__input'

interface HeaderSearchFormProps {
  toggleMenu?: () => void
  isMobile?: boolean
  placeholder?: string
  iconSize?: number
}

const HeaderSearchForm: FunctionComponent<HeaderSearchFormProps> = ({
  toggleMenu,
  isMobile,
  placeholder,
  iconSize,
}) => {
  const appInsights = useAppInsightsContext()
  const navigate = useNavigate()
  const trackSearchEvent = useTrackEvent(appInsights, 'AUTHENTICATED_SEARCH_REQUEST', {})

  const { searchInputValue, setSearchInputValue } = useHeaderSearch()
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<AutoSuggestSearchResult[]>([])
  const [highlightValue, setHighlightValue] = useState(searchInputValue)
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null)
  const { setBackdrop } = useHeaderSearch()

  const ref = useRef<HTMLDivElement>(null)

  const { keyDown } = useTraverseList(
    ref,
    (activeElement, list) => {
      list.forEach((el) => {
        el.classList.remove(ACTIVE_ITEM_CLASS)
      })
      activeElement.classList.add(ACTIVE_ITEM_CLASS)

      setSelectedElement(activeElement)
    },
    {
      rotating: false,
      directChildrenOnly: false,
      horizontally: false,
    },
  )

  const getSuggestions = (query: string, signal: AbortSignal) => {
    fetchSuggestionsFromSearchApi(query, signal)
      .then((res) => {
        setResults(res.result)
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => setLoading(false))
  }

  const onGetSuggestions = (query: string) => {
    const controller = new AbortController()
    const { signal } = controller
    setHighlightValue(query)
    if (isAuthenticated()) {
      trackSearchEvent({ theQuery: query, userGroups: getGroups() })
    }
    if (!signal.aborted) {
      getSuggestions(query, signal)
    }

    return () => {
      controller.abort()
    }
  }

  const showDropDown = (show: boolean) => {
    setShowSuggestions(show)
    setBackdrop(show)
  }

  const debouncedFetchResults = useMemo(() => debounce((query) => onGetSuggestions(query), 300), [])

  useEffect(() => {
    return () => {
      debouncedFetchResults.cancel()
    }
  }, [])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value)
    setSelectedElement(null)

    if (e.target.value?.trim().length >= MIN_QUERY_LENGTH) {
      setLoading(true)
      showDropDown(true)
      debouncedFetchResults(e.target.value)
    } else {
      showDropDown(false)
    }
  }

  const onFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const el = document.querySelector<HTMLAnchorElement>(`.${ACTIVE_ITEM_CLASS}`)

      // If a suggestion is selected use that one, otherwise submit the form
      if (selectedElement && el) {
        document.querySelector<HTMLAnchorElement>(`.${ACTIVE_ITEM_CLASS}`)?.click()
        showDropDown(false)
      } else {
        const config = Object.values(SEARCH_PAGE_CONFIG).find(
          ({ type: configType }) => SearchType.Search === configType,
        )

        if (config) {
          const { pathname } = toSearch()
          const queryString = toSearchParams([[queryParam, searchInputValue.trim()]]).toString()
          navigate(`${pathname as string}?${queryString}`)
        }

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement?.blur()
        }
      }

      // If we're using mobile search don't forget to close the menu
      if (toggleMenu) {
        toggleMenu()
      }
    },
    [searchInputValue, selectedElement],
  )

  const onBlur = () => {
    // Arbitrary 200 ms timeout here, needed since onBlur is triggered before the user can actually click on a link
    setTimeout(() => {
      if (isMobile) {
        return
      }

      showDropDown(false)

      if (toggleMenu) {
        toggleMenu()
      }
    }, 300)
  }

  const onFocus = () => {
    if (searchInputValue.length > 2) {
      showDropDown(true)
      onGetSuggestions(searchInputValue)
    }
    if (!isMobile && toggleMenu) {
      toggleMenu()
    }
  }

  const onClear = () => {
    setSearchInputValue('')
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const { code } = event
      if (code === ESC_KEY) {
        showDropDown(false)
        if (toggleMenu) {
          toggleMenu()
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [onClear])

  useEffect(() => {
    if (searchInputValue === '') {
      showDropDown(false)
    }
  }, [searchInputValue])

  useEffect(() => {
    return () => setBackdrop(false)
  }, [])

  return (
    <StyledForm onSubmit={onFormSubmit} data-testid={isMobile ? 'mobileSearchForm' : 'searchForm'}>
      <StyledLegend>Data zoeken</StyledLegend>
      <SearchBar
        onFocus={onFocus}
        onBlur={onBlur}
        onClear={onClear}
        onChange={onChange}
        value={searchInputValue}
        onKeyDown={keyDown}
        placeholder={placeholder}
        iconSize={iconSize}
        id={SEARCH_BAR_INPUT_ID}
      >
        {showSuggestions && (
          <AutoSuggest
            highlightValue={highlightValue}
            ref={ref}
            loading={loading}
            suggestions={results}
            inputValue={searchInputValue}
            toggleMenu={toggleMenu}
          />
        )}
      </SearchBar>
    </StyledForm>
  )
}

export default HeaderSearchForm
